import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Verified as VerifiedIcon,
  SmartToy as SmartToyIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { fr } from 'date-fns/locale';
import { useTechnicians } from '../../contexts/TechniciansContext';
import { useSkills } from '../../contexts/SkillsContext';
import { useOnCalls } from '../../contexts/OnCallsContext';
import { useAbsences } from '../../contexts/AbsencesContext';
// TODO: Migrer vers les contextes API
// import { skillsDB, techniciansDB, onCallsDB, absencesDB } from '../../db/database';
import { format, addDays, isWithinInterval, addWeeks, startOfYear } from 'date-fns';
import { holidaysService } from '../../services/holidaysService';
import { formatLocalDateToISO } from '../../utils/dateUtils';
import { validateOnCall, ValidationErrorType } from '../../services/onCallValidation';
import TechnicianAssignment from './TechnicianAssignment';
import WeekComplianceDialog from './WeekComplianceDialog';
import AIWeekFillDialog from './AIWeekFillDialog';
import WeekComplianceService from '../../services/weekComplianceService';
import * as XLSX from 'xlsx';

const OnCallSchedule = ({ 
  selectedYear = new Date().getFullYear(),
  periodFilter = 'all', // 'all', 'month', 'quarter', 'semester'
  selectedMonth = new Date().getMonth() + 1, // 1-12
  selectedQuarter = Math.ceil((new Date().getMonth() + 1) / 3), // 1-4
  selectedSemester = new Date().getMonth() < 6 ? 1 : 2, // 1-2
  highlightOnCallId,
  focusWeek,
  highlightWeek, // Nouvelle prop pour surligner une semaine spécifique
  onEditOnCall, // Prop pour éditer une astreinte depuis le planning
  complianceRefreshTrigger = 0, // Trigger pour actualiser la conformité
  onDataChange // Callback pour notifier les changements de données au parent
}) => {
  const { technicians: allTechnicians, loading: techniciansLoading } = useTechnicians();
  const { skills, loading: skillsLoading } = useSkills();
  const { onCalls, addOnCall, updateOnCall, deleteOnCall, loading: onCallsLoading } = useOnCalls();
  const { absences, loading: absencesLoading } = useAbsences();

  const [technicians, setTechnicians] = useState({});
  const [schedule, setSchedule] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [allAbsences, setAllAbsences] = useState([]); // Charger toutes les absences une seule fois
  const [conflictDialog, setConflictDialog] = useState({
    open: false,
    conflicts: [],
    weekNumber: null,
    newTechnicianId: null
  });
  const [validationDialog, setValidationDialog] = useState({
    open: false,
    message: '',
    type: null,
    pendingAssignment: null
  });
  const [isOperationInProgress, setIsOperationInProgress] = useState(false);
  const [weekComplianceStates, setWeekComplianceStates] = useState({}); // État de conformité par semaine
  const [isCheckingCompliance, setIsCheckingCompliance] = useState(false);
  const [complianceDialog, setComplianceDialog] = useState({
    open: false,
    weekCompliance: null
  });
  const [aiFillDialog, setAiFillDialog] = useState({
    open: false,
    weekData: null
  });
  const [deleteWeekDialog, setDeleteWeekDialog] = useState({
    open: false,
    weekData: null,
    isDeleting: false
  });
  const currentWeekRef = useRef(null);
  const highlightedOnCallRef = useRef(null);

  // Fonctions pour la conformité par semaine
  const checkAllWeeksCompliance = useCallback(async () => {
    if (!schedule.length || isCheckingCompliance) return;
    
    setIsCheckingCompliance(true);
    const newComplianceStates = {};
    
    try {
      const techniciansMap = allTechnicians.reduce((acc, tech) => {
        acc[tech.id] = tech;
        return acc;
      }, {});

      for (const week of schedule) {
        const weekCompliance = await WeekComplianceService.checkWeekCompliance(
          week,
          onCalls,
          techniciansMap,
          skills,
          absences,
          holidays,
          onCalls // Toutes les astreintes pour vérification repos
        );
        // 🔧 CORRECTION : Utiliser une clé unique incluant l'année
        const weekKey = `${selectedYear}-${week.weekNumber}`;
        newComplianceStates[weekKey] = weekCompliance;
      }
      
      setWeekComplianceStates(newComplianceStates);
      console.log('✅ Vérification de conformité terminée pour', Object.keys(newComplianceStates).length, 'semaines');
    } catch (error) {
      console.error('❌ Erreur lors de la vérification de conformité:', error);
    } finally {
      setIsCheckingCompliance(false);
    }
  }, [schedule, isCheckingCompliance, allTechnicians, onCalls, skills, absences, holidays, selectedYear]);

  useEffect(() => {
    if (!techniciansLoading && !skillsLoading && !onCallsLoading && !absencesLoading) {
      loadData();
    }
  }, [techniciansLoading, skillsLoading, onCallsLoading, absencesLoading, selectedYear]);

  // Effet pour vérifier automatiquement la conformité après le chargement initial
  useEffect(() => {
    if (schedule.length > 0 && Object.keys(weekComplianceStates).length === 0) {
      // Lancer automatiquement la vérification de conformité au premier chargement
      setTimeout(() => {
        checkAllWeeksCompliance();
      }, 500);
    }
  }, [schedule.length]);

  // 🔧 CORRECTION : Relancer la vérification quand l'année change
  useEffect(() => {
    if (schedule.length > 0 && !isCheckingCompliance) {
      console.log(`🔄 Changement d'année détecté (${selectedYear}), relance de la vérification de conformité`);
      // Vider les états de conformité de l'année précédente
      setWeekComplianceStates({});
      // Relancer la vérification après un court délai
      setTimeout(() => {
        checkAllWeeksCompliance();
      }, 500);
    }
  }, [selectedYear]);

  useEffect(() => {
    if (schedule.length === 0 && !techniciansLoading && !skillsLoading && !onCallsLoading) {
      loadData();
    }
  }, [schedule]);

  // Effet pour centrer automatiquement sur la semaine actuelle lors du premier chargement
  useEffect(() => {
    if (schedule.length > 0 && selectedYear === new Date().getFullYear()) {
      // Attendre que le DOM soit mis à jour avant de faire défiler
      setTimeout(() => {
        if (currentWeekRef.current) {
          currentWeekRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      }, 100);
    }
  }, [schedule, selectedYear]);

  // Effet pour centrer sur une astreinte spécifique quand on navigue depuis le dialog de conflit
  useEffect(() => {
    if (schedule.length > 0 && highlightOnCallId) {
      setTimeout(() => {
        if (highlightedOnCallRef.current) {
          highlightedOnCallRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      }, 100);
    }
  }, [schedule, highlightOnCallId]);

  // Effet pour nettoyer le surlignage après un délai
  useEffect(() => {
    if (highlightOnCallId) {
      const timer = setTimeout(() => {
        // Le surlignage reste visible mais on peut le nettoyer si nécessaire
      }, 3000); // Garder le surlignage 3 secondes
      
      return () => clearTimeout(timer);
    }
  }, [highlightOnCallId]);

  // Effet pour actualiser la conformité quand déclenché depuis l'extérieur
  useEffect(() => {
    if (complianceRefreshTrigger > 0 && schedule.length > 0) {
      // Vider les états de conformité pour forcer une re-vérification
      setWeekComplianceStates({});
      
      // Relancer automatiquement la vérification de toutes les semaines
      setTimeout(async () => {
        await checkAllWeeksCompliance();
      }, 200);
    }
  }, [complianceRefreshTrigger, schedule.length, checkAllWeeksCompliance]);

  const loadData = async () => {
    try {
      console.log('=== CHARGEMENT DES DONNÉES OnCallSchedule ===');
      
      // Charger les jours fériés pour l'année sélectionnée
      const yearHolidays = await holidaysService.getHolidays(selectedYear);

      setHolidays(yearHolidays);

      // Convertir les techniciens en map pour un accès plus facile
      const techniciansMap = allTechnicians.reduce((acc, tech) => {
        acc[tech.id] = tech;
        return acc;
      }, {});
      setTechnicians(techniciansMap);

      console.log('Techniciens chargés:', Object.keys(techniciansMap).length);
      console.log('Compétences chargées:', skills.length);
      console.log('Astreintes chargées:', onCalls.length);
      console.log('Absences chargées:', absences.length);

      // Générer les données du planning avec les astreintes
      generateSchedule(onCalls, yearHolidays);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    }
  };

  // Mémoriser les absences par technicien pour éviter les recalculs
  const absencesByTechnician = useMemo(() => {
    const absencesMap = {};
    absences.forEach(absence => {
      if (!absencesMap[absence.technicianId]) {
        absencesMap[absence.technicianId] = [];
      }
      absencesMap[absence.technicianId].push(absence);
    });
    return absencesMap;
  }, [absences]);

  // Fonction pour trouver le premier vendredi de l'année
  const getFirstFridayOfYear = (year) => {
    const firstDay = startOfYear(new Date(year, 0, 1));
    let firstFriday = firstDay;
    
    // Trouver le premier vendredi (5 = vendredi)
    while (firstFriday.getDay() !== 5) {
      firstFriday = addDays(firstFriday, 1);
    }
    
    return firstFriday;
  };

  // Mémoriser la génération du planning
  const generateSchedule = useCallback((onCallsData, holidaysData) => {
    const scheduleData = [];

    // Commencer par le premier vendredi de l'année sélectionnée
    const firstFriday = getFirstFridayOfYear(selectedYear);

    // Générer 52 semaines d'astreinte (vendredi à jeudi)
    for (let week = 0; week < 52; week++) {
      const weekStart = addWeeks(firstFriday, week); // Vendredi
      const weekEnd = addDays(weekStart, 6); // Jeudi suivant

      // Trouver les jours fériés de la période
      const weekHolidays = [];
      for (let i = 0; i <= 6; i++) {
        const currentDay = addDays(weekStart, i);
        const holidayName = holidaysService.getHolidayName(currentDay, holidaysData);
        if (holidayName) {
          weekHolidays.push({
            date: currentDay,
            name: holidayName
          });
        }
      }

      // Trouver les astreintes qui couvrent cette période
      const weekAssignments = {};
      onCallsData.forEach(onCall => {
        const onCallStart = new Date(onCall.startDate);
        const onCallEnd = new Date(onCall.endDate);
        
        // Vérifier si l'astreinte couvre cette période
        if (isWithinInterval(weekStart, { start: onCallStart, end: onCallEnd }) ||
            isWithinInterval(weekEnd, { start: onCallStart, end: onCallEnd }) ||
            isWithinInterval(onCallStart, { start: weekStart, end: weekEnd })) {
          
          // Assigner le technicien à chaque compétence qu'il couvre
          onCall.skills.forEach(skillId => {
            weekAssignments[skillId] = onCall.technicianId;
          });
        }
      });

      scheduleData.push({
        semester: getSemester(weekStart),
        startDate: weekStart,
        endDate: weekEnd,
        weekNumber: week + 1, // Numéroter à partir de 1
        assignments: weekAssignments,
        holidays: weekHolidays
      });
    }

    setSchedule(scheduleData);
  }, [selectedYear]);

  // Fonction pour détecter les conflits
  const detectConflicts = (weekNumber, newTechnicianId, newTechnicianSkills) => {
    const conflicts = [];
    const weekData = schedule.find(week => week.weekNumber === weekNumber);
    if (!weekData) return conflicts;

    // Pour chaque compétence du nouveau technicien
    newTechnicianSkills.forEach(skillId => {
      // Vérifier s'il y a déjà des techniciens assignés à cette compétence
      const existingAssignments = onCalls.filter(onCall => {
        const onCallStart = new Date(onCall.startDate);
        const onCallEnd = new Date(onCall.endDate);
        const weekStart = weekData.startDate;
        const weekEnd = weekData.endDate;
        
        return onCall.skills.includes(skillId) &&
               onCall.technicianId !== newTechnicianId &&
               ((onCallStart <= weekStart && onCallEnd >= weekStart) ||
                (onCallStart <= weekEnd && onCallEnd >= weekEnd) ||
                (onCallStart >= weekStart && onCallEnd <= weekEnd));
      });

      if (existingAssignments.length > 0) {
        const skill = skills.find(s => s.id === skillId);
        conflicts.push({
          skillId,
          skillName: skill?.name || 'Compétence inconnue',
          existingTechnicians: existingAssignments.map(assignment => ({
            technicianId: assignment.technicianId,
            technician: technicians[assignment.technicianId],
            onCallId: assignment.id
          })),
          newTechnician: {
            technicianId: newTechnicianId,
            technician: technicians[newTechnicianId]
          }
        });
      }
    });

    return conflicts;
  };

  const handleAssignTechnician = useCallback(async (weekNumber, skillId, technicianId) => {
    try {
      console.log(`🚨 DÉBUT handleAssignTechnician:`, { weekNumber, skillId, technicianId });
      
      // Trouver la période correspondante
      const weekData = schedule.find(week => week.weekNumber === weekNumber);
      if (!weekData) {
        console.log(`❌ weekData introuvable pour semaine ${weekNumber}`);
        return;
      }
      
      console.log(`📅 WEEKDATA trouvé pour semaine ${weekNumber}:`, {
        weekNumber: weekData.weekNumber,
        startDate: weekData.startDate.toISOString(),
        endDate: weekData.endDate.toISOString(),
        startDateLocal: weekData.startDate.toLocaleDateString('fr-FR'),
        endDateLocal: weekData.endDate.toLocaleDateString('fr-FR')
      });

      // Récupérer le technicien pour connaître ses compétences
      const technician = technicians[technicianId];
      if (!technician) return;

      // Filtrer les compétences du technicien qui correspondent aux compétences disponibles
      const technicianSkillIds = (technician.skills || []).map((item) =>
        typeof item === 'object' ? (item.skillId ?? item.id) : item
      );
      const technicianSkillsForThisPeriod = technicianSkillIds.filter((id) =>
        skills.some((skill) => skill.id === id)
      );

      // Préparer les données pour la validation
      // 🔧 CORRECTION : Formatter directement les dates locales sans conversion UTC
      // Les dates weekData.startDate et weekData.endDate sont déjà en heure locale (créées par addWeeks/addDays)
      // On les formate directement en YYYY-MM-DD sans passer par toISOString() qui les convertirait en UTC
      const startDateStr = formatLocalDateToISO(weekData.startDate);
      const endDateStr = formatLocalDateToISO(weekData.endDate);
      
      console.log(`🚨 DATES CRÉATION ASTREINTE:`, {
        original_startDate: weekData.startDate.toISOString(),
        original_endDate: weekData.endDate.toISOString(),
        startDateLocal: weekData.startDate.toLocaleDateString('fr-FR'),
        endDateLocal: weekData.endDate.toLocaleDateString('fr-FR'),
        final_startDate: startDateStr,
        final_endDate: endDateStr
      });
      
      const onCallData = {
        technicianId,
        startDate: startDateStr,
        endDate: endDateStr,
        skills: technicianSkillsForThisPeriod
      };

      console.log(`🚨 VUE PLANNING - onCallData créé:`, {
        weekData: {
          startDate: weekData.startDate.toISOString(),
          endDate: weekData.endDate.toISOString(),
          startDateLocal: weekData.startDate.toLocaleDateString('fr-FR'),
          endDateLocal: weekData.endDate.toLocaleDateString('fr-FR')
        },
        onCallData,
        technicianId
      });

      // Valider l'astreinte (absences et période de repos)
      const validation = await validateOnCall(onCallData, onCalls, absences);

      if (!validation.isValid) {
        if (validation.type === ValidationErrorType.JTL_WARNING) {
          // JTL warning - proposer de continuer quand même
          setValidationDialog({
            open: true,
            message: validation.message,
            type: validation.type,
            pendingAssignment: {
              weekNumber,
              skillId,
              technicianId,
              weekData,
              technicianSkillsForThisPeriod,
              onCallData
            }
          });
          return;
        } else {
          // Erreur bloquante (absence non-JTL ou période de repos insuffisante)
          setValidationDialog({
            open: true,
            message: validation.message,
            type: validation.type,
            pendingAssignment: null
          });
          return;
        }
      }

      // Validation réussie, détecter les conflits de compétences
      const conflicts = detectConflicts(weekNumber, technicianId, technicianSkillsForThisPeriod);

      if (conflicts.length > 0) {
        // Il y a des conflits, ouvrir la boîte de dialogue
        setConflictDialog({
          open: true,
          conflicts,
          weekNumber,
          newTechnicianId: technicianId,
          pendingAssignment: {
            weekNumber,
            skillId,
            technicianId,
            weekData,
            technicianSkillsForThisPeriod,
            onCallData
          }
        });
        return;
      }

      // Pas de conflit, procéder normalement
      await executeAssignment(weekNumber, skillId, technicianId, weekData, technicianSkillsForThisPeriod);

    } catch (error) {
      console.error('Erreur lors de l\'assignation du technicien:', error);
    }
  }, [schedule, technicians, skills, onCalls, absences]);

  const executeAssignment = async (weekNumber, skillId, technicianId, weekData, technicianSkillsForThisPeriod) => {
    setIsOperationInProgress(true);
    try {
      console.log('🔄 Exécution assignation:', { weekNumber, skillId, technicianId, technicianSkillsForThisPeriod });
      
      // Obtenir les données les plus récentes directement des contextes
      const currentOnCalls = onCalls; // Les contextes sont déjà à jour
      
      // 🔧 CORRECTION : Formatter les dates locales correctement
      const startDateStr = formatLocalDateToISO(weekData.startDate);
      const endDateStr = formatLocalDateToISO(weekData.endDate);
      
      // Vérifier s'il existe déjà une astreinte pour ce technicien sur cette période
      // 🔧 CORRECTION : Extraire uniquement la partie date (YYYY-MM-DD) pour la comparaison
      // car les dates en base sont au format ISO complet (2026-01-02T17:00:00.000Z)
      const existingOnCall = currentOnCalls.find(onCall => {
        const onCallStartDate = typeof onCall.startDate === 'string' ? onCall.startDate.split('T')[0] : onCall.startDate;
        const onCallEndDate = typeof onCall.endDate === 'string' ? onCall.endDate.split('T')[0] : onCall.endDate;
        
        return onCall.technicianId === technicianId &&
          onCallStartDate === startDateStr &&
          onCallEndDate === endDateStr;
      });
      
      console.log('🔍 Astreinte existante trouvée:', existingOnCall);
      console.log('📊 Total astreintes dans le contexte:', currentOnCalls.length);

      let updatedOnCall;
      if (existingOnCall) {
        // Mettre à jour l'astreinte existante en ajoutant les nouvelles compétences
        const updatedSkills = [...new Set([...existingOnCall.skills, ...technicianSkillsForThisPeriod])];
        updatedOnCall = {
          ...existingOnCall,
          skills: updatedSkills
        };
        await updateOnCall(existingOnCall.id, updatedOnCall);
      } else {
        // Créer une nouvelle astreinte avec toutes les compétences du technicien
        const newOnCall = {
          technicianId,
          startDate: startDateStr,
          endDate: endDateStr,
          skills: technicianSkillsForThisPeriod
        };

        updatedOnCall = await addOnCall(newOnCall);
      }

      // Mettre à jour le state local immédiatement (optimisation optimiste)
      // Note: setOnCalls n'est pas disponible dans le contexte, donc on recharge simplement les données
      // On peut optimiser cela plus tard si nécessaire

      // Mettre à jour le planning local immédiatement
      setSchedule(prevSchedule => {
        return prevSchedule.map(week => {
          if (week.weekNumber === weekNumber) {
            const newAssignments = { ...week.assignments };
            // Assigner le technicien à toutes ses compétences pour cette semaine
            technicianSkillsForThisPeriod.forEach(sId => {
              newAssignments[sId] = technicianId;
            });
            return {
              ...week,
              assignments: newAssignments
            };
          }
          return week;
        });
      });

      // Recharger les données pour s'assurer de la cohérence et forcer la réactualisation des contextes
      if (onDataChange) {
        await onDataChange(); // Déclencher le rechargement des contextes depuis le parent
      }
      await loadData(); // Puis recharger les données locales
      
      // Revérifier la conformité de la semaine affectée automatiquement
      setTimeout(async () => {
        // Attendre que les données soient rechargées
        await new Promise(resolve => setTimeout(resolve, 200));
        
        const updatedSchedule = schedule;
        const affectedWeek = updatedSchedule.find(w => w.weekNumber === weekNumber);
        if (affectedWeek) {
          console.log(`🔄 Re-vérification automatique de la conformité de la semaine ${weekNumber}`);
          await checkSingleWeekCompliance(affectedWeek);
        }
      }, 400);

    } catch (error) {
      console.error('Erreur lors de l\'assignation:', error);
      // En cas d'erreur, recharger les données pour être sûr de la cohérence
      await loadData();
    } finally {
      setIsOperationInProgress(false);
    }
  };

  const handleConflictResolution = async (conflictIndex, keepNewTechnician) => {
    const conflict = conflictDialog.conflicts[conflictIndex];
    const { pendingAssignment } = conflictDialog;

    console.log('🔄 handleConflictResolution appelé:', {
      conflictIndex,
      keepNewTechnician,
      conflict,
      pendingAssignment
    });

    try {
      if (keepNewTechnician) {
        console.log('🔧 Remplacement: suppression des anciens techniciens pour la compétence', conflict.skillId);
        
        // Supprimer d'abord toutes les anciennes assignations pour cette compétence
        for (const existing of conflict.existingTechnicians) {
          console.log(`   ❌ Suppression de ${existing.technicianId} pour compétence ${conflict.skillId}`);
          await handleRemoveTechnician(
            pendingAssignment.weekNumber,
            conflict.skillId, 
            existing.technicianId
          );
        }
        
        console.log('✅ Suppression terminée, rechargement forcé du contexte...');
        
        // 🔧 CORRECTION : Forcer un rechargement complet du contexte après la suppression
        // pour éviter que executeAssignment ne trouve encore l'ancienne astreinte
        if (onDataChange) {
          await onDataChange();
        }
        await loadData();
        
        // Attendre un court délai supplémentaire pour que React mette à jour les contextes
        await new Promise(resolve => setTimeout(resolve, 200));
        
        console.log('✅ Contexte rechargé, on va maintenant ajouter le nouveau technicien');
      } else {
        console.log('📝 Conservation: retrait de la compétence du nouveau technicien');
        // Retirer cette compétence des compétences à assigner au nouveau technicien
        const updatedSkills = pendingAssignment.technicianSkillsForThisPeriod.filter(
          skillId => skillId !== conflict.skillId
        );
        pendingAssignment.technicianSkillsForThisPeriod = updatedSkills;
      }

      // Traiter le conflit suivant ou finaliser l'assignation
      const remainingConflicts = conflictDialog.conflicts.slice(conflictIndex + 1);
      
      if (remainingConflicts.length > 0) {
        console.log('⏭️ Conflits restants:', remainingConflicts.length);
        setConflictDialog(prev => ({
          ...prev,
          conflicts: remainingConflicts
        }));
      } else {
        console.log('✅ Plus de conflits, finalisation...');
        // Plus de conflits, fermer la boîte de dialogue
        setConflictDialog({ open: false, conflicts: [], weekNumber: null, newTechnicianId: null });
        
        // Exécuter l'assignation seulement s'il reste des compétences à assigner
        if (pendingAssignment.technicianSkillsForThisPeriod.length > 0) {
          console.log('🚀 Exécution de l\'assignation avec compétences:', pendingAssignment.technicianSkillsForThisPeriod);
          // Exécuter l'assignation directement (pas besoin de recharger)
          await executeAssignment(
            pendingAssignment.weekNumber,
            pendingAssignment.skillId,
            pendingAssignment.technicianId,
            pendingAssignment.weekData,
            pendingAssignment.technicianSkillsForThisPeriod
          );
        } else {
          console.log('ℹ️ Aucune compétence à assigner');
        }
      }
    } catch (error) {
      console.error('❌ Erreur lors de la résolution du conflit:', error);
    }
  };

  const handleCancelConflictResolution = () => {
    setConflictDialog({ open: false, conflicts: [], weekNumber: null, newTechnicianId: null });
  };

  const handleValidationConfirm = async () => {
    const { pendingAssignment } = validationDialog;
    if (pendingAssignment) {
      // L'utilisateur veut continuer malgré le JTL warning
      setValidationDialog({ open: false, message: '', type: null, pendingAssignment: null });
      
      // Détecter les conflits de compétences
      const conflicts = detectConflicts(
        pendingAssignment.weekNumber, 
        pendingAssignment.technicianId, 
        pendingAssignment.technicianSkillsForThisPeriod
      );

      if (conflicts.length > 0) {
        // Il y a des conflits, ouvrir la boîte de dialogue
        setConflictDialog({
          open: true,
          conflicts,
          weekNumber: pendingAssignment.weekNumber,
          newTechnicianId: pendingAssignment.technicianId,
          pendingAssignment: pendingAssignment
        });
        return;
      }

      // Pas de conflit, procéder
      await executeAssignment(
        pendingAssignment.weekNumber,
        pendingAssignment.skillId,
        pendingAssignment.technicianId,
        pendingAssignment.weekData,
        pendingAssignment.technicianSkillsForThisPeriod
      );
    }
  };

  const handleValidationCancel = () => {
    setValidationDialog({ open: false, message: '', type: null, pendingAssignment: null });
  };


  const checkSingleWeekCompliance = useCallback(async (weekData) => {
    try {
      const techniciansMap = allTechnicians.reduce((acc, tech) => {
        acc[tech.id] = tech;
        return acc;
      }, {});

      // Recharger les données fraîches pour la vérification
      const weekCompliance = await WeekComplianceService.checkWeekCompliance(
        weekData,
        onCalls,
        techniciansMap,
        skills,
        absences,
        holidays,
        onCalls
      );
      
      console.log(`✅ Conformité mise à jour pour la semaine ${weekData.weekNumber}:`, weekCompliance.status);
      
      // 🔧 CORRECTION : Utiliser une clé unique incluant l'année
      const weekKey = `${selectedYear}-${weekData.weekNumber}`;
      setWeekComplianceStates(prev => ({
        ...prev,
        [weekKey]: weekCompliance
      }));
      
      return weekCompliance;
    } catch (error) {
      console.error('❌ Erreur lors de la vérification de conformité de la semaine:', error);
      return null;
    }
  }, [allTechnicians, onCalls, skills, absences, holidays, selectedYear]);

  const handleOpenComplianceDialog = async (weekData) => {
    // 🔧 CORRECTION : Utiliser une clé unique incluant l'année
    const weekKey = `${selectedYear}-${weekData.weekNumber}`;
    const weekCompliance = weekComplianceStates[weekKey] || 
                          await checkSingleWeekCompliance(weekData);
    
    if (weekCompliance) {
      setComplianceDialog({
        open: true,
        weekCompliance: weekCompliance
      });
    }
  };

  const handleCloseComplianceDialog = () => {
    setComplianceDialog({
      open: false,
      weekCompliance: null
    });
  };

  const handleNavigateToOnCallFromCompliance = (onCallId, weekStart) => {
    handleCloseComplianceDialog();
    // Logique de navigation similar à ConflictCheckDialog
    if (onEditOnCall && onCallId) {
      const onCall = onCalls.find(oc => oc.id === onCallId);
      if (onCall) {
        onEditOnCall(onCall);
      }
    }
  };

  const handleOpenAIFillDialog = (weekData) => {
    setAiFillDialog({
      open: true,
      weekData: weekData
    });
  };

  const handleCloseAIFillDialog = () => {
    setAiFillDialog({
      open: false,
      weekData: null
    });
  };

  const handleAIFillSuccess = async () => {
    handleCloseAIFillDialog();
    // Recharger les données après le remplissage IA
    if (onDataChange) {
      await onDataChange();
    }
    await loadData();
    // Revérifier la conformité de toutes les semaines
    setTimeout(async () => {
      await checkAllWeeksCompliance();
    }, 300);
  };

  // Fonctions pour la suppression des astreintes de la semaine
  const handleOpenDeleteWeekDialog = (weekData) => {
    setDeleteWeekDialog({
      open: true,
      weekData: weekData,
      isDeleting: false
    });
  };

  const handleCloseDeleteWeekDialog = () => {
    setDeleteWeekDialog({
      open: false,
      weekData: null,
      isDeleting: false
    });
  };

  const handleConfirmDeleteWeek = async () => {
    if (!deleteWeekDialog.weekData) return;

    setDeleteWeekDialog(prev => ({ ...prev, isDeleting: true }));

    try {
      const weekData = deleteWeekDialog.weekData;
      
      // Trouver toutes les astreintes qui couvrent cette semaine
      const weekOnCalls = onCalls.filter(onCall => {
        const onCallStart = new Date(onCall.startDate);
        const onCallEnd = new Date(onCall.endDate);
        const weekStart = new Date(weekData.startDate);
        const weekEnd = new Date(weekData.endDate);
        
        // Vérifier si l'astreinte couvre cette période
        return isWithinInterval(weekStart, { start: onCallStart, end: onCallEnd }) ||
               isWithinInterval(weekEnd, { start: onCallStart, end: onCallEnd }) ||
               isWithinInterval(onCallStart, { start: weekStart, end: weekEnd });
      });

      console.log(`🗑️ Suppression de ${weekOnCalls.length} astreinte(s) pour la semaine ${weekData.weekNumber}`);

      // Supprimer chaque astreinte
      for (const onCall of weekOnCalls) {
        await deleteOnCall(onCall.id);
        console.log(`✅ Astreinte supprimée: ${onCall.id} (${technicians[onCall.technicianId]?.firstName} ${technicians[onCall.technicianId]?.lastName})`);
      }

      // Fermer le dialog
      handleCloseDeleteWeekDialog();

      // Actualiser la conformité de la semaine
      setTimeout(() => {
        checkSingleWeekCompliance(weekData);
      }, 500);

      // Notifier le parent du changement
      if (onDataChange) {
        onDataChange();
      }

      console.log(`✅ Suppression terminée pour la semaine ${weekData.weekNumber}`);

    } catch (error) {
      console.error('❌ Erreur lors de la suppression des astreintes de la semaine:', error);
    } finally {
      setDeleteWeekDialog(prev => ({ ...prev, isDeleting: false }));
    }
  };

  const handleRemoveTechnician = useCallback(async (weekNumber, skillId, technicianId = null) => {
    setIsOperationInProgress(true);
    try {
      console.log('🗑️ Suppression technicien:', { weekNumber, skillId, technicianId });
      
      // Trouver la période correspondante
      const weekData = schedule.find(week => week.weekNumber === weekNumber);
      if (!weekData) {
        console.warn('Semaine non trouvée:', weekNumber);
        return;
      }

      // Chercher l'astreinte existante
      let existingOnCall;
      
      // 🔧 CORRECTION : Formatter les dates locales correctement (comme lors de la création)
      const targetStartDate = formatLocalDateToISO(weekData.startDate);
      const targetEndDate = formatLocalDateToISO(weekData.endDate);
      
      console.log('🔍 Recherche astreinte à supprimer:', {
        technicianId,
        skillId,
        targetStartDate,
        targetEndDate,
        totalOnCalls: onCalls.length,
        weekDataOriginal: {
          startDate: weekData.startDate.toISOString(),
          endDate: weekData.endDate.toISOString(),
          startDateLocal: weekData.startDate.toLocaleDateString('fr-FR'),
          endDateLocal: weekData.endDate.toLocaleDateString('fr-FR')
        }
      });
      
      // 🔧 DEBUG : Afficher TOUTES les astreintes pour ce technicien et cette compétence
      console.log('📋 Toutes les astreintes en mémoire pour technicianId=' + technicianId + ' skillId=' + skillId + ':');
      onCalls.forEach(onCall => {
        if (onCall.technicianId === technicianId && onCall.skills.includes(skillId)) {
          console.log('  ✅ Astreinte ID=' + onCall.id + ':', {
            startDate: onCall.startDate,
            endDate: onCall.endDate,
            skills: onCall.skills
          });
        }
      });
      
      if (technicianId) {
        // Suppression d'un technicien spécifique pour cette compétence
        // 🔧 CORRECTION : Extraire uniquement la partie date (YYYY-MM-DD) pour la comparaison
        // car les dates en base sont au format ISO complet (2026-01-02T17:00:00.000Z)
        existingOnCall = onCalls.find(onCall => {
          const onCallStartDate = typeof onCall.startDate === 'string' ? onCall.startDate.split('T')[0] : onCall.startDate;
          const onCallEndDate = typeof onCall.endDate === 'string' ? onCall.endDate.split('T')[0] : onCall.endDate;
          
          return onCall.technicianId === technicianId &&
            onCallStartDate === targetStartDate &&
            onCallEndDate === targetEndDate &&
            onCall.skills.includes(skillId);
        });
      } else {
        // Suppression basée uniquement sur la période et la compétence
        // 🔧 CORRECTION : Extraire uniquement la partie date (YYYY-MM-DD) pour la comparaison
        existingOnCall = onCalls.find(onCall => {
          const onCallStartDate = typeof onCall.startDate === 'string' ? onCall.startDate.split('T')[0] : onCall.startDate;
          const onCallEndDate = typeof onCall.endDate === 'string' ? onCall.endDate.split('T')[0] : onCall.endDate;
          
          return onCallStartDate === targetStartDate &&
            onCallEndDate === targetEndDate &&
            onCall.skills.includes(skillId);
        });
      }
      
      console.log('🔍 Astreinte trouvée pour suppression:', existingOnCall);

      if (existingOnCall) {
        console.log('Astreinte trouvée à modifier:', existingOnCall);
        
        // Retirer la compétence de l'astreinte
        const updatedSkills = existingOnCall.skills.filter(id => id !== skillId);
        
        if (updatedSkills.length === 0) {
          // Supprimer complètement l'astreinte si plus aucune compétence
          console.log('Suppression complète de l\'astreinte');
          await deleteOnCall(existingOnCall.id);
        } else {
          // Mettre à jour l'astreinte en retirant la compétence
          console.log('Mise à jour de l\'astreinte, nouvelles compétences:', updatedSkills);
          const updatedOnCall = {
            ...existingOnCall,
            skills: updatedSkills
          };
          await updateOnCall(existingOnCall.id, updatedOnCall);
        }

        // Recharger les données pour mettre à jour l'affichage et forcer la réactualisation des contextes
        if (onDataChange) {
          await onDataChange(); // Déclencher le rechargement des contextes depuis le parent
        }
        await loadData(); // Puis recharger les données locales
        
        // Revérifier la conformité de la semaine affectée automatiquement
        setTimeout(async () => {
          // Attendre que les données soient rechargées
          await new Promise(resolve => setTimeout(resolve, 200));
          
          const updatedSchedule = schedule;
          const affectedWeek = updatedSchedule.find(w => w.weekNumber === weekNumber);
          if (affectedWeek) {
            console.log(`🔄 Re-vérification automatique de la conformité de la semaine ${weekNumber} après suppression`);
            await checkSingleWeekCompliance(affectedWeek);
          }
        }, 400);
        
        console.log('✅ Suppression réussie');
      } else {
        console.warn('Aucune astreinte trouvée pour ces critères');
      }
    } catch (error) {
      console.error('❌ Erreur lors de la suppression du technicien:', error);
    } finally {
      setIsOperationInProgress(false);
    }
  }, [schedule, onCalls, deleteOnCall, updateOnCall, loadData]);

  const getSemester = (date) => {
    const month = date.getMonth();
    return month < 6 ? 'S1' : 'S2';
  };

  const formatDateRange = (startDate, endDate) => {
    return `${format(startDate, 'dd/MM', { locale: fr })} au ${format(endDate, 'dd/MM', { locale: fr })}`;
  };

  const renderHolidays = (weekHolidays) => {
    if (weekHolidays.length === 0) return null;
    
    return (
      <Tooltip title={weekHolidays.map(h => 
        `${format(h.date, 'dd/MM')} - ${h.name}`
      ).join('\n')} arrow>
        <Box sx={{ 
          display: 'flex', 
          gap: 0.5,
          flexWrap: 'wrap'
        }}>
          {weekHolidays.map((holiday, index) => (
            <Typography 
              key={index} 
              variant="caption" 
              sx={{ 
                bgcolor: 'error.light',
                color: 'white',
                px: 1,
                borderRadius: 1,
                whiteSpace: 'nowrap'
              }}
            >
              {format(holiday.date, 'dd/MM')}
            </Typography>
          ))}
        </Box>
      </Tooltip>
    );
  };

  // Fonction pour rendre les indicateurs de conformité et IA
  const renderWeekActions = (week) => {
    // 🔧 CORRECTION : Utiliser une clé unique incluant l'année
    const weekKey = `${selectedYear}-${week.weekNumber}`;
    const weekCompliance = weekComplianceStates[weekKey];
    
    // Icône de conformité
    const getComplianceIcon = () => {
      if (!weekCompliance) {
        return <VerifiedIcon sx={{ fontSize: '1rem', color: 'text.disabled' }} />;
      }
      switch (weekCompliance.status) {
        case 'valid':
          return <CheckCircleIcon sx={{ fontSize: '1rem', color: 'success.main' }} />;
        case 'warning':
          return <WarningIcon sx={{ fontSize: '1rem', color: 'warning.main' }} />;
        case 'error':
          return <ErrorIcon sx={{ fontSize: '1rem', color: 'error.main' }} />;
        default:
          return <VerifiedIcon sx={{ fontSize: '1rem', color: 'text.disabled' }} />;
      }
    };

    const getComplianceTooltip = () => {
      if (!weekCompliance) {
        return 'Cliquer pour vérifier la conformité de cette semaine';
      }
      switch (weekCompliance.status) {
        case 'valid':
          return 'Semaine conforme - Cliquer pour voir les détails';
        case 'warning':
          return `${weekCompliance.summary.warningIssues} avertissement(s) - Cliquer pour voir les détails`;
        case 'error':
          return `${weekCompliance.summary.criticalIssues} erreur(s) critique(s) - Cliquer pour voir les détails`;
        default:
          return 'Cliquer pour vérifier la conformité';
      }
    };

    return (
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        {/* Bouton IA - Compléter automatiquement */}
        <Tooltip title="IA : Compléter automatiquement les affectations manquantes de cette semaine">
          <span>
            <IconButton 
              size="small" 
              onClick={() => handleOpenAIFillDialog(week)}
              sx={{ p: 0.25 }}
              disabled={isOperationInProgress}
            >
              <SmartToyIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
            </IconButton>
          </span>
        </Tooltip>
        
        {/* Bouton de conformité */}
        <Tooltip title={getComplianceTooltip()}>
          <IconButton 
            size="small" 
            onClick={() => handleOpenComplianceDialog(week)}
            sx={{ p: 0.25 }}
          >
            {getComplianceIcon()}
          </IconButton>
        </Tooltip>

        {/* Bouton de suppression des astreintes de la semaine */}
        <Tooltip title="Supprimer toutes les astreintes de cette semaine">
          <span>
            <IconButton 
              size="small" 
              onClick={() => handleOpenDeleteWeekDialog(week)}
              sx={{ p: 0.25 }}
              disabled={isOperationInProgress}
            >
              <DeleteIcon sx={{ fontSize: '1rem', color: 'error.main' }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  };

  // Filtrer les semaines selon le filtre de période sélectionné
  const visibleSchedule = useMemo(() => {
    if (periodFilter === 'all') {
      return schedule;
    }

    return schedule.filter(week => {
      const weekStart = new Date(week.startDate);
      const weekMonth = weekStart.getMonth() + 1; // 1-12

      if (periodFilter === 'month') {
        return weekMonth === selectedMonth;
      }

      if (periodFilter === 'quarter') {
        const weekQuarter = Math.ceil(weekMonth / 3);
        return weekQuarter === selectedQuarter;
      }

      if (periodFilter === 'semester') {
        const weekSemester = weekMonth <= 6 ? 1 : 2;
        return weekSemester === selectedSemester;
      }

      return true;
    });
  }, [schedule, periodFilter, selectedMonth, selectedQuarter, selectedSemester]);

  const handleExportXLSX = useCallback(() => {
    try {
      // Feuille 1: liste d'astreintes
      const skillsById = skills.reduce((acc, s) => { acc[String(s.id)] = s; return acc; }, {});
      const skillsByName = skills.reduce((acc, s) => { acc[String(s.name).toLowerCase()] = s; return acc; }, {});
      const techniciansMap = Object.fromEntries(Object.entries(technicians).map(([k, v]) => [k, v]));
      const normalizeSkillToken = (token) => {
        if (token == null) return '';
        if (typeof token === 'object') {
          const raw = token.skillId ?? token.id;
          if (raw == null) return '';
          const key = String(raw);
          return skillsById[key]?.name || key;
        }
        if (typeof token === 'number') {
          const key = String(token);
          return skillsById[key]?.name || key;
        }
        // string
        const t = String(token).trim();
        if (/^\d+$/.test(t)) {
          const key = String(parseInt(t, 10));
          return skillsById[key]?.name || key;
        }
        // lookup by name (case-insensitive)
        const byName = skillsByName[t.toLowerCase()];
        return byName?.name || t;
      };
      const listRows = onCalls.map((o) => {
        const names = (o.skills || []).map((it) => normalizeSkillToken(it)).filter(Boolean);
        const uniqueNames = Array.from(new Set(names));
        return {
        'Date début': format(new Date(o.startDate), 'dd/MM/yyyy', { locale: fr }),
        'Date fin': format(new Date(o.endDate), 'dd/MM/yyyy', { locale: fr }),
        'Technicien': techniciansMap[o.technicianId] ? `${techniciansMap[o.technicianId].firstName} ${techniciansMap[o.technicianId].lastName}` : o.technicianId,
        'Compétences couvertes': uniqueNames.join(', ')
        };
      });

      const wsList = XLSX.utils.json_to_sheet(listRows);

      // Feuille 2: vue planning (colonnes visibles)
      const header = ['Semestre', 'Période', 'Semaine', ...skills.map((s) => s.name), 'Jours fériés'];
      const planningRows = visibleSchedule.map((week) => {
        const row = [];
        row.push(week.semester);
        row.push(`${format(week.startDate, 'dd/MM', { locale: fr })} au ${format(week.endDate, 'dd/MM', { locale: fr })}`);
        row.push(`S${week.weekNumber}`);
        skills.forEach((skill) => {
          const techId = week.assignments[skill.id];
          if (techId && technicians[techId]) {
            row.push(`${technicians[techId].firstName} ${technicians[techId].lastName}`);
          } else {
            row.push('');
          }
        });
        const holidaysText = (week.holidays || []).map((h) => `${format(h.date, 'dd/MM', { locale: fr })} ${h.name}`).join(', ');
        row.push(holidaysText);
        return row;
      });
      const wsPlanning = XLSX.utils.aoa_to_sheet([header, ...planningRows]);

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, wsList, 'Astreintes');
      XLSX.utils.book_append_sheet(wb, wsPlanning, 'Planning');
      const fileName = `planning-astreintes-${format(new Date(), 'yyyyMMdd-HHmm')}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (e) {
      console.error('❌ Export XLSX échoué:', e);
    }
  }, [onCalls, technicians, skills, visibleSchedule]);

  // Écouteur pour bouton d'export global (dans l'en-tête de page)
  useEffect(() => {
    const handler = () => handleExportXLSX();
    window.addEventListener('export-planning-xlsx', handler);
    // Expose une fonction globale comme fallback
    window.__exportPlanningXLSX = handleExportXLSX;
    return () => {
      window.removeEventListener('export-planning-xlsx', handler);
      if (window.__exportPlanningXLSX === handleExportXLSX) {
        try { delete window.__exportPlanningXLSX; } catch (_) { window.__exportPlanningXLSX = undefined; }
      }
    };
  }, [handleExportXLSX]);

  // Mémoriser le rendu des lignes pour éviter les re-rendus inutiles
  const memoizedRows = useMemo(() => {
    const currentDate = new Date();
    
    return visibleSchedule.map((week) => {
      // Vérifier si c'est la semaine actuelle (seulement si on affiche l'année courante)
      const isCurrentWeek = selectedYear === currentDate.getFullYear() && 
                           currentDate >= week.startDate && currentDate <= week.endDate;
      
      // Vérifier si cette semaine contient l'astreinte surlignée OU si c'est la semaine à surligner
      const isHighlightedWeek = 
        // Surlignage par astreinte spécifique
        (highlightOnCallId && 
          (Object.values(week.assignments).some(techId => {
            const weekOnCalls = onCalls.filter(oc => 
              oc.technicianId === techId && 
              oc.startDate >= week.startDate && 
              oc.startDate <= week.endDate
            );
            return weekOnCalls.some(oc => oc.id === highlightOnCallId);
          }) || 
          // Vérifier aussi directement dans toutes les astreintes de la semaine
          onCalls.some(oc => 
            oc.id === highlightOnCallId && 
            isWithinInterval(new Date(oc.startDate), { start: week.startDate, end: week.endDate })
          ))
        ) ||
        // Surlignage par semaine spécifique (pour manque de couverture, etc.)
        (highlightWeek && 
          isWithinInterval(new Date(highlightWeek), { start: week.startDate, end: week.endDate })
        );
      
      const backgroundColor = isHighlightedWeek ? 'warning.light' : (isCurrentWeek ? 'primary.light' : 'inherit');
      const fontWeight = (isHighlightedWeek || isCurrentWeek) ? 'bold' : 'normal';
      
      return (
        <TableRow 
          key={week.weekNumber}
          ref={isHighlightedWeek ? highlightedOnCallRef : (isCurrentWeek ? currentWeekRef : null)}
          sx={{
            backgroundColor: backgroundColor,
            '& .MuiTableCell-root': {
              backgroundColor: backgroundColor,
              fontWeight: fontWeight
            }
          }}
        >
          <TableCell sx={{ fontSize: '0.75rem', textAlign: 'center' }}>
            {renderWeekActions(week)}
          </TableCell>
          <TableCell sx={{ fontSize: '0.75rem' }}>
            {week.semester}
            {isCurrentWeek && ' 📅'}
            {isHighlightedWeek && ' 🔍'}
          </TableCell>
          <TableCell sx={{ fontSize: '0.75rem' }}>{formatDateRange(week.startDate, week.endDate)}</TableCell>
          <TableCell sx={{ fontSize: '0.75rem' }}>S{week.weekNumber}</TableCell>
        {[...skills].sort((a, b) => a.name.localeCompare(b.name)).map((skill) => (
          <TableCell key={skill.id} sx={{ p: 0.5 }}>
            <TechnicianAssignment
              technicians={technicians}
              skillId={skill.id}
              weekData={week}
              onAssign={handleAssignTechnician}
              onRemove={handleRemoveTechnician}
              currentAssignment={week.assignments[skill.id]}
              schedule={schedule}
              onCalls={onCalls}
              skills={skills}
              absencesByTechnician={absencesByTechnician}
              isOperationInProgress={isOperationInProgress}
              onEditOnCall={onEditOnCall}
            />
          </TableCell>
        ))}
        <TableCell sx={{ p: 0.5 }}>
          {renderHolidays(week.holidays)}
        </TableCell>
      </TableRow>
    );
     });
   }, [visibleSchedule, skills, technicians, onCalls, absencesByTechnician, handleAssignTechnician, handleRemoveTechnician, selectedYear, highlightOnCallId]);

  return (
    <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 200px)' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 80, maxWidth: 80, textAlign: 'center' }}>
              <Tooltip title="Vérifier la conformité de toutes les semaines">
                <span>
                  <IconButton 
                    size="small" 
                    onClick={checkAllWeeksCompliance}
                    disabled={isCheckingCompliance || !schedule.length}
                    sx={{ p: 0.25 }}
                  >
                    {isCheckingCompliance ? (
                      <CircularProgress size={16} />
                    ) : (
                      <VerifiedIcon sx={{ fontSize: '1rem' }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </TableCell>
            <TableCell sx={{ minWidth: 80, maxWidth: 80 }}>Semestre</TableCell>
            <TableCell sx={{ minWidth: 120, maxWidth: 120 }}>Période</TableCell>
            <TableCell sx={{ minWidth: 80, maxWidth: 80 }}>Semaine</TableCell>
            {[...skills].sort((a, b) => a.name.localeCompare(b.name)).map((skill) => (
              <TableCell key={skill.id} sx={{ minWidth: 100, maxWidth: 150, fontWeight: 'bold' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      backgroundColor: skill.color,
                      borderRadius: '50%',
                      flexShrink: 0
                    }}
                  />
                  <Typography variant="inherit" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                    {skill.name}
                  </Typography>
                </Box>
              </TableCell>
            ))}
            <TableCell sx={{ minWidth: 120, maxWidth: 150 }}>Jours fériés</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {memoizedRows}
        </TableBody>
      </Table>
             {conflictDialog.open && conflictDialog.conflicts.length > 0 && (() => {
               // Extraire les noms des techniciens pour les labels
               const newTechName = conflictDialog.newTechnicianId && technicians[conflictDialog.newTechnicianId] 
                 ? `${technicians[conflictDialog.newTechnicianId].firstName} ${technicians[conflictDialog.newTechnicianId].lastName}`
                 : 'Nouveau technicien';
               
               const existingTechName = conflictDialog.conflicts[0]?.existingTechnicians[0]?.technician
                 ? `${conflictDialog.conflicts[0].existingTechnicians[0].technician.firstName} ${conflictDialog.conflicts[0].existingTechnicians[0].technician.lastName}`
                 : 'Technicien actuel';
               
               return (
         <Dialog
           open={conflictDialog.open}
           onClose={handleCancelConflictResolution}
           maxWidth="sm"
           fullWidth
         >
           <DialogTitle>
             Conflit détecté - {conflictDialog.conflicts[0]?.skillName}
           </DialogTitle>
           <DialogContent>
             <Typography variant="body1" sx={{ mb: 2 }}>
               <strong>{newTechName}</strong> remplacera <strong>{existingTechName}</strong> sur {conflictDialog.conflicts[0]?.skillName}.
             </Typography>
           </DialogContent>
           <DialogActions>
             <Button onClick={handleCancelConflictResolution}>
               Annuler
             </Button>
             <Button 
               onClick={() => handleConflictResolution(0, false)}
               variant="outlined"
             >
               Garder {existingTechName}
             </Button>
             <Button 
               onClick={() => handleConflictResolution(0, true)}
               variant="contained"
               color="primary"
             >
               Remplacer {existingTechName} par {newTechName}
             </Button>
           </DialogActions>
         </Dialog>
               );
             })()}

        {/* Dialogue de validation des absences */}
        <Dialog
          open={validationDialog.open}
          onClose={handleValidationCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {validationDialog.type === ValidationErrorType.JTL_WARNING 
              ? "Attention - JTL détecté" 
              : "Impossible d'affecter le technicien"}
          </DialogTitle>
          <DialogContent>
            <Alert 
              severity={validationDialog.type === ValidationErrorType.JTL_WARNING ? "warning" : "error"}
              sx={{ mb: 2 }}
            >
              {validationDialog.message}
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleValidationCancel}>
              Annuler
            </Button>
            {validationDialog.type === ValidationErrorType.JTL_WARNING && (
              <Button 
                onClick={handleValidationConfirm} 
                variant="contained" 
                color="warning"
              >
                Confirmer quand même
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Dialog de conformité pour une semaine */}
        <WeekComplianceDialog
          open={complianceDialog.open}
          onClose={handleCloseComplianceDialog}
          weekCompliance={complianceDialog.weekCompliance}
          technicians={technicians}
          skills={skills}
          selectedYear={selectedYear}
          onNavigateToOnCall={handleNavigateToOnCallFromCompliance}
        />

        {/* Dialog IA pour compléter automatiquement une semaine */}
        <AIWeekFillDialog
          open={aiFillDialog.open}
          onClose={handleCloseAIFillDialog}
          weekData={aiFillDialog.weekData}
          onSuccess={handleAIFillSuccess}
        />

        {/* Dialog de confirmation de suppression des astreintes de la semaine */}
        <Dialog
          open={deleteWeekDialog.open}
          onClose={handleCloseDeleteWeekDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DeleteIcon color="error" />
              <Typography variant="h6">
                Supprimer les astreintes de la semaine
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            {deleteWeekDialog.weekData && (
              <>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Attention :</strong> Cette action supprimera définitivement toutes les astreintes 
                    de la semaine {deleteWeekDialog.weekData.weekNumber} 
                    ({format(new Date(deleteWeekDialog.weekData.startDate), 'dd/MM/yyyy', { locale: fr })} - {format(new Date(deleteWeekDialog.weekData.endDate), 'dd/MM/yyyy', { locale: fr })}).
                  </Typography>
                </Alert>
                <Typography variant="body2" color="text.secondary">
                  Les astreintes suivantes seront supprimées :
                </Typography>
                <Box sx={{ mt: 1, pl: 2 }}>
                  {onCalls.filter(onCall => {
                    const onCallStart = new Date(onCall.startDate);
                    const onCallEnd = new Date(onCall.endDate);
                    const weekStart = new Date(deleteWeekDialog.weekData.startDate);
                    const weekEnd = new Date(deleteWeekDialog.weekData.endDate);
                    
                    return isWithinInterval(weekStart, { start: onCallStart, end: onCallEnd }) ||
                           isWithinInterval(weekEnd, { start: onCallStart, end: onCallEnd }) ||
                           isWithinInterval(onCallStart, { start: weekStart, end: weekEnd });
                  }).map(onCall => (
                    <Typography key={onCall.id} variant="body2" sx={{ mb: 0.5 }}>
                      • {technicians[onCall.technicianId]?.firstName} {technicians[onCall.technicianId]?.lastName} 
                      ({onCall.skills.map(skillId => skills.find(s => s.id === skillId)?.name).filter(Boolean).join(', ')})
                    </Typography>
                  ))}
                  {onCalls.filter(onCall => {
                    const onCallStart = new Date(onCall.startDate);
                    const onCallEnd = new Date(onCall.endDate);
                    const weekStart = new Date(deleteWeekDialog.weekData.startDate);
                    const weekEnd = new Date(deleteWeekDialog.weekData.endDate);
                    
                    return isWithinInterval(weekStart, { start: onCallStart, end: onCallEnd }) ||
                           isWithinInterval(weekEnd, { start: onCallStart, end: onCallEnd }) ||
                           isWithinInterval(onCallStart, { start: weekStart, end: weekEnd });
                  }).length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      Aucune astreinte à supprimer pour cette semaine.
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleCloseDeleteWeekDialog}
              disabled={deleteWeekDialog.isDeleting}
            >
              Annuler
            </Button>
            <Button 
              onClick={handleConfirmDeleteWeek}
              variant="contained"
              color="error"
              disabled={deleteWeekDialog.isDeleting}
              startIcon={deleteWeekDialog.isDeleting ? <CircularProgress size={16} /> : <DeleteIcon />}
            >
              {deleteWeekDialog.isDeleting ? 'Suppression...' : 'Supprimer'}
            </Button>
          </DialogActions>
        </Dialog>
      </TableContainer>
    );
  };

export default OnCallSchedule; 